import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Paper, Grid, Typography, TextField, Button, MenuItem, Checkbox, FormControlLabel,
  CircularProgress, Table, TableHead, TableBody, TableRow, TableCell, Chip, Divider,
} from '@material-ui/core';
import { listSpecs, getSpec, saveSpec, listSqlSources, convertSqlToSpec } from '../../../Actions';
import { applyMapping, specColumns } from '../../../utils/mappingEngine';

const MATCHING_TYPES = [
  { value: '', label: 'Standard (no special matching)' },
  { value: 'rfms_id_match_sku_partial', label: 'RFMS — SKU, partial (upsert)' },
  { value: 'rfms_id_match_sku_full', label: 'RFMS — SKU, full (replace/remove unmatched)' },
  { value: 'rfms_id_match_product_partial', label: 'RFMS — product, partial (upsert)' },
  { value: 'rfms_id_match_product_full', label: 'RFMS — product, full (replace/remove unmatched)' },
  { value: 'pacific_solutions_id_match_sku_partial', label: 'Pacific Solutions — SKU, partial' },
];

const STARTER = JSON.stringify({
  version: 1,
  name: 'NEW VENDOR',
  outputColumns: ['distributor', 'manufacturer', 'style_number', 'sku', 'color_name', 'cost', 'uom_type', 'basis_cd'],
  rowFilters: [{ col: 'Cost Column', op: 'nonEmptyNonZero' }],
  outputs: [
    {
      fields: {
        distributor: { from: 'Supplier' },
        manufacturer: { from: 'Manufacturer' },
        style_number: { from: 'Style Number' },
        sku: { from: 'Color' },
        color_name: { from: 'Color' },
        cost: { from: 'Cost Column' },
        uom_type: { from: 'Units' },
        basis_cd: { const: 'ST' },
      },
    },
  ],
}, null, 2);

// Every source column a spec references (from/concat/expr/if/when/rowFilters) — used to flag typos
// against the sample's headers.
function referencedColumns(spec) {
  const cols = new Set();
  const addFilters = (fs) => (fs || []).forEach((f) => f?.col && cols.add(f.col));
  const addExpr = (e) => {
    if (!e || typeof e !== 'object') return;
    if (e.col) cols.add(e.col);
    ['add', 'sub', 'mul', 'coalesce'].forEach((k) => Array.isArray(e[k]) && e[k].forEach(addExpr));
  };
  const addRule = (r) => {
    if (!r || typeof r !== 'object') return;
    if (r.from) cols.add(r.from);
    if (Array.isArray(r.concat)) r.concat.forEach((c) => cols.add(c));
    if (r.expr) addExpr(r.expr);
    if (r.if) { addFilters(r.if); addRule(r.then); addRule(r.else); }
  };
  addFilters(spec.rowFilters);
  (spec.outputs || []).forEach((t) => {
    addFilters(t.when);
    Object.values(t.fields || {}).forEach(addRule);
  });
  return [...cols];
}

export default function MappingBuilder() {
  const [specList, setSpecList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [matchingType, setMatchingType] = useState('');
  const [active, setActive] = useState(true);
  const [specText, setSpecText] = useState(STARTER);

  const [sampleHeaders, setSampleHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [sampleName, setSampleName] = useState('');

  const [preview, setPreview] = useState(null); // { cols, rows, total }
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [sqlSources, setSqlSources] = useState([]);
  const [sqlSourceId, setSqlSourceId] = useState('');
  const [converting, setConverting] = useState(false);
  const fileRef = useRef(null);

  async function refreshList() {
    const res = await listSpecs().catch(() => null);
    setSpecList(res?.data ?? []);
  }
  useEffect(() => {
    refreshList();
    listSqlSources().then((r) => setSqlSources(r?.data ?? [])).catch(() => {});
  }, []);

  async function convertFromSql() {
    if (!sqlSourceId) return;
    setError(''); setMsg(''); setPreview(''); setConverting(true);
    const res = await convertSqlToSpec(sqlSourceId).then((r) => r.data).catch(() => null);
    setConverting(false);
    if (!res?.spec) { setError('Could not convert that SQL transform.'); return; }
    setSelectedId('');
    setName(res.spec.name || '');
    setMatchingType('');
    setActive(true);
    setSpecText(JSON.stringify(res.spec, null, 2));
    const warns = res.warnings || [];
    setMsg(warns.length
      ? `Draft generated with ${warns.length} thing(s) to review: ${warns.slice(0, 5).join('; ')}${warns.length > 5 ? ' …' : ''}. Set the matching strategy, review, then Save.`
      : 'Draft generated cleanly. Set the matching strategy, review against a sample, then Save.');
  }

  async function loadSpec(id) {
    setSelectedId(id);
    setError(''); setMsg(''); setPreview(null);
    if (!id) { setName(''); setMatchingType(''); setActive(true); setSpecText(STARTER); return; }
    const res = await getSpec(id).catch(() => null);
    if (!res?.data) { setError('Failed to load spec.'); return; }
    const row = res.data;
    setName(row.name || '');
    setMatchingType(row.matching_type || '');
    setActive(row.active_ind !== 0);
    const spec = typeof row.spec === 'string' ? JSON.parse(row.spec) : row.spec;
    setSpecText(JSON.stringify(spec, null, 2));
  }

  function onSampleFile(file) {
    if (!file) return;
    setSampleName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const objs = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      setSampleHeaders((aoa[0] || Object.keys(objs[0] || {})).map(String));
      setSampleRows(objs.slice(0, 500));
    };
    reader.readAsArrayBuffer(file);
  }

  function runPreview() {
    setError(''); setMsg('');
    let spec;
    try { spec = JSON.parse(specText); } catch (e) { setError('Spec is not valid JSON: ' + e.message); return; }
    if (!Array.isArray(spec.outputs) || !spec.outputs.length) { setError('Spec needs a non-empty "outputs" array.'); return; }
    if (!sampleRows.length) { setError('Upload a sample file first.'); return; }
    try {
      const out = applyMapping(spec, sampleRows);
      const cols = specColumns(spec);
      setPreview({ cols, rows: out.slice(0, 100), total: out.length, sampleIn: sampleRows.length });
    } catch (e) { setError('Preview failed: ' + e.message); }
  }

  const missingCols = useMemo(() => {
    if (!sampleHeaders.length) return [];
    let spec; try { spec = JSON.parse(specText); } catch { return []; }
    const hs = new Set(sampleHeaders);
    return referencedColumns(spec).filter((c) => !hs.has(c));
  }, [specText, sampleHeaders]);

  async function save() {
    setError(''); setMsg('');
    let spec;
    try { spec = JSON.parse(specText); } catch (e) { setError('Spec is not valid JSON: ' + e.message); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    const res = await saveSpec({ id: selectedId || undefined, name: name.trim(), spec, matchingType, active })
      .catch((err) => err?.response ?? null);
    setSaving(false);
    if (!res || res.status >= 400) { setError(res?.data?.message || 'Save failed.'); return; }
    setMsg(`Saved "${name.trim()}".`);
    if (!selectedId && res.data?.id) setSelectedId(String(res.data.id));
    refreshList();
  }

  return (
    <div style={{ padding: '30px 20px' }}>
      <Paper style={{ padding: '20px' }}>
        <Typography variant="h4" gutterBottom>Mapping Rule Builder</Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Build a declarative mapping for a structured ERP/vendor file (RFMS, QFloors, …). Upload a sample,
          author the spec, preview against the sample, then save. Files for this vendor are transformed by
          the processor and imported under the vendor's match engine.
        </Typography>

        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={12} md={3}>
            <TextField select fullWidth label="Spec" value={selectedId} onChange={(e) => loadSpec(e.target.value)}>
              <MenuItem value="">— New spec —</MenuItem>
              {specList.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>{s.name}{s.active_ind === 0 ? ' (inactive)' : ''}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth label="Vendor / transform name" value={name} onChange={(e) => setName(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth label="Matching strategy" value={matchingType} onChange={(e) => setMatchingType(e.target.value)}>
              {MATCHING_TYPES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControlLabel control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Active" />
          </Grid>
        </Grid>

        {sqlSources.length ? (
          <Grid container spacing={2} alignItems="center" style={{ marginTop: 4 }}>
            <Grid item xs={12} md={5}>
              <TextField select fullWidth size="small" label="Start from a legacy SQL/JSON transform" value={sqlSourceId} onChange={(e) => setSqlSourceId(e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {sqlSources.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.name}{s.transform_type ? ` (${s.transform_type})` : ''}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button variant="outlined" onClick={convertFromSql} disabled={!sqlSourceId || converting}>
                {converting ? <CircularProgress size={18} /> : 'Convert to draft'}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="textSecondary">
                Auto-generates a draft spec from the old SQL — review it, set the matching strategy, preview against a sample, then Save.
              </Typography>
            </Grid>
          </Grid>
        ) : null}

        <Divider style={{ margin: '16px 0' }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Button variant="outlined" onClick={() => fileRef.current?.click()}>Upload sample file</Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
              onChange={(e) => onSampleFile(e.target.files?.[0])} />
            {sampleName ? <Typography variant="caption" style={{ marginLeft: 10 }}>{sampleName} — {sampleRows.length} sample rows</Typography> : null}
            {sampleHeaders.length ? (
              <div style={{ marginTop: 10, maxHeight: 140, overflow: 'auto' }}>
                <Typography variant="caption" color="textSecondary">Source columns ({sampleHeaders.length}):</Typography>
                <div>{sampleHeaders.map((h) => <Chip key={h} label={h} size="small" style={{ margin: 2 }} />)}</div>
              </div>
            ) : null}
            {missingCols.length ? (
              <Typography variant="caption" style={{ color: '#b26a00', display: 'block', marginTop: 8 }}>
                Spec references columns not in the sample: {missingCols.join(', ')}
              </Typography>
            ) : null}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Spec (JSON)" multiline minRows={14} fullWidth variant="outlined"
              value={specText} onChange={(e) => setSpecText(e.target.value)}
              InputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
            />
          </Grid>
        </Grid>

        <div style={{ marginTop: 16 }}>
          <Button variant="contained" onClick={runPreview} style={{ marginRight: 10 }}>Preview against sample</Button>
          <Button variant="contained" color="primary" onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save spec'}
          </Button>
        </div>

        {error ? <Typography style={{ color: '#f44336', marginTop: 12 }}>{error}</Typography> : null}
        {msg ? <Typography style={{ color: '#388E3C', marginTop: 12 }}>{msg}</Typography> : null}

        {preview ? (
          <div style={{ marginTop: 16 }}>
            <Typography variant="body2" color="textSecondary">
              {preview.total} output row{preview.total === 1 ? '' : 's'} from {preview.sampleIn} sample rows
              {' '}({preview.cols.length} columns). Showing first {preview.rows.length}.
            </Typography>
            <div style={{ overflow: 'auto', maxHeight: 380, border: '1px solid #eee', marginTop: 8 }}>
              <Table size="small" stickyHeader style={{ width: 'auto', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>{preview.cols.map((c) => <TableCell key={c} style={{ fontWeight: 'bold', whiteSpace: 'nowrap', padding: '4px 12px', background: '#fafafa' }}>{c}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((r, i) => (
                    <TableRow key={i}>{preview.cols.map((c) => <TableCell key={c} style={{ whiteSpace: 'nowrap', padding: '4px 12px' }}>{r[c]}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </Paper>
    </div>
  );
}
